'use client'

import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ACCOUNT_CONNECTION_EMPTY_CLASS } from '@/features/dashboard/components/account-connection/styles'
import {
  canSubmit,
  type HlSelectionState,
  toggleSubaccount,
} from '@/lib/hyperliquid/subaccount-selection'
import { cn } from '@/lib/utils'
import type { HyperliquidSubaccountInfo } from '@/types/generated/public_api'

type HyperliquidSubaccountPickerProps = {
  subaccounts: HyperliquidSubaccountInfo[]
  remainingSlots: number
  selection: HlSelectionState
  onChange: (selection: HlSelectionState) => void
  onSubmit: () => void
  submitting: boolean
  isPageLayout?: boolean
}

export function HyperliquidSubaccountPicker({
  subaccounts,
  remainingSlots,
  selection,
  onChange,
  onSubmit,
  submitting,
  isPageLayout = false,
}: HyperliquidSubaccountPickerProps) {
  const t = useTranslations('accountConnection')
  const submittable = canSubmit(selection, remainingSlots)

  return (
    <div className="flex flex-col gap-4">
      {subaccounts.length === 0 ? (
        <p className={ACCOUNT_CONNECTION_EMPTY_CLASS}>{t('hyperliquidPickerEmpty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {subaccounts.map((sub) => {
            const checked = selection.selected.includes(sub.address)
            const disabled =
              sub.alreadyLinked || (!checked && selection.selected.length >= remainingSlots)
            return (
              <label
                key={sub.address}
                className={cn(
                  'flex items-center gap-3 border border-white/10 bg-white/5 p-3 text-left',
                  isPageLayout && 'border-foreground/10 bg-foreground/5',
                  disabled ? 'opacity-60' : 'cursor-pointer'
                )}
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={() =>
                    onChange(
                      toggleSubaccount(
                        selection,
                        sub.address,
                        { alreadyLinked: sub.alreadyLinked },
                        remainingSlots
                      )
                    )
                  }
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-sm font-medium">{sub.name || sub.address}</span>
                  {sub.alreadyLinked && (
                    <span className="border-foreground/15 bg-foreground/8 text-foreground w-fit shrink-0 border px-2 py-0.5 text-[10px] font-medium">
                      {t('hyperliquidSubaccountConnected')}
                    </span>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      )}

      <p className={cn('text-center text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
        {t('hyperliquidSlotsMeter', { used: selection.selected.length, max: remainingSlots })}
      </p>

      <Button
        variant="basic"
        type="button"
        className="mx-auto w-fit"
        onClick={onSubmit}
        disabled={!submittable || submitting}
      >
        {submitting ? t('hyperliquidLinking') : t('hyperliquidLinkButton')}
      </Button>
    </div>
  )
}
